import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, WifiOff } from "lucide-react";
import { api } from "../../lib/api";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { enqueue } from "../../lib/offlineQueue";
import { cacheGet } from "../../lib/offlineCache";
import ChoiceStep from "./steps/ChoiceStep";
import InviteSearchStep from "./steps/InviteSearchStep";
import DetailsStep from "./steps/DetailsStep";
import ListAlertStep from "./steps/ListAlertStep";
import PhotoIdStep from "./steps/PhotoIdStep";
import BriefingStep from "./steps/BriefingStep";
import BadgeStep from "./steps/BadgeStep";
import OfflineQueuedStep from "./steps/OfflineQueuedStep";

export default function CheckInFlow() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [step, setStep] = useState("choice");
  const [inviteData, setInviteData] = useState(null);
  const [visit, setVisit] = useState(null);
  const [offlineJob, setOfflineJob] = useState(null);
  const [queuedSummary, setQueuedSummary] = useState(null);

  const handleChoice = (type) => {
    setInviteData(null);
    setStep(type === "prereg" ? "invite-search" : "details");
  };

  const handleInviteSelect = (inv) => {
    setInviteData({
      visitor_name: inv.visitor_name,
      visitor_phone: inv.visitor_phone || "",
      visitor_email: inv.visitor_email || "",
      visitor_company: inv.visitor_company || "",
      host_id: inv.host_id,
      purpose: inv.purpose,
      gate_id: inv.gate_id || "",
      invite_id: inv.id,
    });
    setStep("details");
  };

  const handleDetails = async (form) => {
    if (!isOnline) {
      setOfflineJob({ form });
      setStep("photo");
      return;
    }
    const { data } = await api.post("/visits", form);
    setVisit(data);
    setStep(data.status === "blocked" || data.list_match?.matched ? "alert" : "photo");
  };

  const handleOverride = () => setStep("photo");
  const handleCancel = () => {
    setVisit(null);
    setInviteData(null);
    setOfflineJob(null);
    setStep("choice");
  };

  const handlePhotoNext = (data) => {
    if (!isOnline) setOfflineJob((prev) => ({ ...prev, ...data }));
    setStep("briefing");
  };

  const handleBriefingNext = (signatures) => {
    if (!isOnline) {
      const job = { ...offlineJob, signatures: signatures || [] };
      enqueue(job);
      const hostMatch = cacheGet("hosts").find((h) => h.id === job.form.host_id);
      setQueuedSummary({ visitor_name: job.form.visitor_name, host_name: hostMatch?.name || "Host", purpose: job.form.purpose });
      setOfflineJob(null);
      setStep("offline-queued");
      return;
    }
    setStep("badge");
  };

  const handleBack = () => {
    if (step === "choice") navigate("/kiosk");
    else setStep("choice");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-brand-navy p-8" data-testid="checkin-flow-page">
      {!isOnline && step !== "offline-queued" && (
        <div className="max-w-2xl mx-auto mb-6 flex items-center gap-2 bg-amber-50 border border-amber-300 text-amber-700 text-sm font-semibold px-4 py-2 rounded-md" data-testid="offline-banner">
          <WifiOff size={16} /> Offline — this check-in will be saved on this tablet and synced automatically once reconnected.
        </div>
      )}
      {step !== "alert" && step !== "offline-queued" && (
        <button onClick={handleBack} data-testid="checkin-back-button" className="flex items-center gap-2 text-slate-500 hover:text-brand-navy mb-8 text-sm uppercase tracking-widest">
          <ArrowLeft size={16} /> Back
        </button>
      )}
      {step === "choice" && <ChoiceStep onChoose={handleChoice} />}
      {step === "invite-search" && <InviteSearchStep onSelect={handleInviteSelect} />}
      {step === "details" && <DetailsStep onNext={handleDetails} initialData={inviteData} />}
      {step === "alert" && visit && <ListAlertStep visit={visit} onOverride={handleOverride} onCancel={handleCancel} />}
      {step === "photo" && (visit || offlineJob) && <PhotoIdStep visitId={visit?.id} offline={!isOnline} onNext={handlePhotoNext} />}
      {step === "briefing" && (visit || offlineJob) && <BriefingStep visitId={visit?.id} offline={!isOnline} onNext={handleBriefingNext} />}
      {step === "badge" && visit && <BadgeStep visitId={visit.id} visitorName={visit.visitor_name} hostName={visit.host_name} purpose={visit.purpose} />}
      {step === "offline-queued" && queuedSummary && (
        <OfflineQueuedStep visitorName={queuedSummary.visitor_name} hostName={queuedSummary.host_name} purpose={queuedSummary.purpose} />
      )}
    </div>
  );
}
